import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { customColumnKey, type CustomObservationDefinition } from './customDefinitions';
export function CustomObservationEditor({
  definitions,
  published,
  onChange,
}: {
  definitions: CustomObservationDefinition[];
  published: CustomObservationDefinition[];
  onChange: (definitions: CustomObservationDefinition[]) => void;
}) {
  function patch(index: number, change: Partial<CustomObservationDefinition>) {
    onChange(definitions.map((d, i) => (i === index ? { ...d, ...change } : d)));
  }
  return (
    <section className="space-y-4">
      <h3>Practice-specific observation columns</h3>
      <p>
        Published schemas remain readable and immutable. Create a new version to change their meaning. Suggestions never
        fill a clinical field automatically.
      </p>
      {definitions.map((d, index) => {
        const locked = published.some((p) => p.event_type === d.event_type && p.version === d.version);
        return (
          <fieldset key={index} className="space-y-3 rounded border p-3">
            <legend>
              {d.label || 'New custom observation'} · version {d.version}
            </legend>
            <div className="grid gap-3 md:grid-cols-2">
              <Field>
                <FieldLabel>Custom identifier {index + 1}</FieldLabel>
                <Input
                  aria-label={`Custom identifier ${index + 1}`}
                  value={d.event_type}
                  disabled={locked}
                  onChange={(e) => patch(index, { event_type: e.target.value as `CUSTOM.${string}` })}
                />
              </Field>
              <Field>
                <FieldLabel>Custom label {index + 1}</FieldLabel>
                <Input
                  aria-label={`Custom label ${index + 1}`}
                  value={d.label}
                  disabled={locked}
                  onChange={(e) => patch(index, { label: e.target.value })}
                />
              </Field>
              <Field>
                <FieldLabel>Subject</FieldLabel>
                <Select
                  value={d.subject}
                  disabled={locked}
                  onValueChange={(v) => patch(index, { subject: v as 'MOTHER' | 'FETUS' })}>
                  <SelectTrigger aria-label={`Custom subject ${index + 1}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="MOTHER">Mother</SelectItem>
                      <SelectItem value="FETUS">Each fetus</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel>Value type</FieldLabel>
                <Select
                  value={d.value_type}
                  disabled={locked}
                  onValueChange={(v) =>
                    patch(index, {
                      value_type: v as CustomObservationDefinition['value_type'],
                      choices: [],
                      unit: null,
                      suggested_value: null,
                    })
                  }>
                  <SelectTrigger aria-label={`Custom value type ${index + 1}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {['string', 'number', 'boolean', 'enum'].map((v) => (
                        <SelectItem value={v} key={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              {d.value_type === 'number' && (
                <Field>
                  <FieldLabel>Units</FieldLabel>
                  <Input
                    aria-label={`Custom units ${index + 1}`}
                    value={d.unit ?? ''}
                    disabled={locked}
                    onChange={(e) => patch(index, { unit: e.target.value || null })}
                  />
                </Field>
              )}
              {d.value_type === 'enum' && (
                <Field>
                  <FieldLabel>Choices, one per line</FieldLabel>
                  <Textarea
                    aria-label={`Custom choices ${index + 1}`}
                    value={d.choices.join('\n')}
                    disabled={locked}
                    onChange={(e) => patch(index, { choices: e.target.value.split('\n') })}
                  />
                </Field>
              )}
              <Field>
                <FieldLabel>Optional suggestion (explicit clinician choice)</FieldLabel>
                <Input
                  aria-label={`Custom suggestion ${index + 1}`}
                  disabled={locked}
                  value={d.suggested_value === null || d.suggested_value === undefined ? '' : String(d.suggested_value)}
                  onChange={(e) => {
                    const v = e.target.value;
                    patch(index, {
                      suggested_value: !v
                        ? null
                        : d.value_type === 'number'
                          ? Number(v)
                          : d.value_type === 'boolean'
                            ? v === 'true'
                              ? true
                              : v === 'false'
                                ? false
                                : v
                            : v,
                    });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Boolean suggestions use true or false. Leave blank for no suggestion.
                </p>
              </Field>
            </div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={d.enabled}
                onCheckedChange={(checked) =>
                  onChange(
                    definitions.map((candidate, i) =>
                      i === index
                        ? { ...candidate, enabled: checked === true }
                        : checked && candidate.event_type === d.event_type
                          ? { ...candidate, enabled: false }
                          : candidate,
                    ),
                  )
                }
              />
              Enable after clinical review
            </label>
            <div className="flex gap-2">
              {locked ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    onChange([
                      ...definitions,
                      {
                        ...d,
                        version:
                          Math.max(...definitions.filter((v) => v.event_type === d.event_type).map((v) => v.version)) +
                          1,
                        enabled: false,
                      },
                    ])
                  }>
                  Create new schema version
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onChange(definitions.filter((_, i) => i !== index))}>
                  Remove unpublished definition
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Column key: {customColumnKey(d)}
              {locked ? ' · published' : ''}
            </p>
          </fieldset>
        );
      })}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          onChange([
            ...definitions,
            {
              event_type: `CUSTOM.FIELD_${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
              version: 1,
              label: '',
              subject: 'MOTHER',
              value_type: 'string',
              choices: [],
              enabled: false,
            },
          ])
        }>
        Add custom observation
      </Button>
    </section>
  );
}
export function ColumnLabelEditor({
  columns,
  labels,
  onChange,
}: {
  columns: { key: string; label: string }[];
  labels: Record<string, string>;
  onChange: (labels: Record<string, string>) => void;
}) {
  return (
    <details>
      <summary>Column display labels</summary>
      <div className="grid gap-3 md:grid-cols-2">
        {columns.map((c) => (
          <Field key={c.key}>
            <FieldLabel htmlFor={`column-label-${c.key}`}>{c.label}</FieldLabel>
            <Input
              id={`column-label-${c.key}`}
              value={labels[c.key] ?? ''}
              placeholder={c.label}
              maxLength={100}
              onChange={(e) => {
                const next = { ...labels };
                if (e.target.value) next[c.key] = e.target.value;
                else delete next[c.key];
                onChange(next);
              }}
            />
          </Field>
        ))}
      </div>
    </details>
  );
}
